import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Grid,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as HealthyIcon,
} from '@mui/icons-material';
import { adminApi, AwsHealth, AwsHealthWindow } from '../services/api';
import {
  Severity,
  Sparkline,
  severityColor,
  severityFromAbsolute,
  severityFromPercent,
  severityIcon,
} from '../components/aws-health/severity';

const REFRESH_INTERVAL_MS = 30_000;
const DEFAULT_WINDOW: AwsHealthWindow = '30d';
const WINDOW_LABELS: Record<AwsHealthWindow, string> = {
  '1h': '1h',
  '24h': '24h',
  '7d': '7d',
  '30d': '30d',
};

const AwsHealthPage: React.FC = () => {
  const [health, setHealth] = useState<AwsHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  // Note: named `selectedWindow` (not `window`) to avoid shadowing the browser's
  // global `window` — used elsewhere in this component for setInterval / addEventListener.
  const [selectedWindow, setSelectedWindow] = useState<AwsHealthWindow>(DEFAULT_WINDOW);
  const inFlightRef = useRef(false);

  const fetchHealth = useCallback(async (sw: AwsHealthWindow) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setRefreshing(true);
    try {
      const result = await adminApi.getAwsHealth(sw);
      setHealth(result.data);
      setError(null);
      setLastFetched(new Date());
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load AWS health';
      setError(message);
    } finally {
      setLoading(false);
      setRefreshing(false);
      inFlightRef.current = false;
    }
  }, []);

  const handleWindowChange = useCallback(
    (_e: React.MouseEvent<HTMLElement>, value: AwsHealthWindow | null) => {
      if (!value) return; // Empty selection — keep current
      setSelectedWindow(value);
    },
    [],
  );

  // Re-fetch when the window changes
  useEffect(() => {
    void fetchHealth(selectedWindow);
  }, [selectedWindow, fetchHealth]);

  // Auto-refresh every 30s, paused when tab is hidden.
  useEffect(() => {
    let intervalId: number | undefined;

    const start = () => {
      if (intervalId === undefined) {
        intervalId = window.setInterval(() => void fetchHealth(selectedWindow), REFRESH_INTERVAL_MS);
      }
    };
    const stop = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void fetchHealth(selectedWindow);
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchHealth, selectedWindow]);

  if (loading && !health) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            AWS Resources
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Platform-level health for the team to spot saturation before it affects users.
            Auto-refreshes every 30s.
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={2}>
          {refreshing && <CircularProgress size={20} />}
          {lastFetched && (
            <Typography variant="caption" color="text.secondary" data-testid="last-fetched">
              Last updated {lastFetched.toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Refresh now">
            <RefreshIcon
              sx={{ cursor: 'pointer', color: 'text.secondary' }}
              onClick={() => void fetchHealth(selectedWindow)}
            />
          </Tooltip>
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Window:
        </Typography>
        <ToggleButtonGroup
          value={selectedWindow}
          exclusive
          onChange={handleWindowChange}
          size="small"
          data-testid="window-selector"
        >
          {(Object.keys(WINDOW_LABELS) as AwsHealthWindow[]).map((w) => (
            <ToggleButton key={w} value={w} data-testid={`window-${w}`}>
              {WINDOW_LABELS[w]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          Totals + sparkline use this window. SQS depth, DLQ contents, and SES (24h) are point-in-time / per-day.
        </Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Couldn't load AWS health</AlertTitle>
          {error}
        </Alert>
      )}

      {health && <PartialDataBanner errors={health.errors} />}

      {health && (
        <>
          <Tier1Panels health={health} />
          <Tier2Panels health={health} />
          <DatabasePanel health={health} />
          <CognitoPanel health={health} />
          <LambdaFunctionsPanel health={health} />
          <SqsPanel health={health} />
          <ActivityPanel health={health} />
        </>
      )}
    </Box>
  );
};

const PartialDataBanner: React.FC<{ errors: AwsHealth['errors'] }> = ({ errors }) => {
  const failed: string[] = [];
  if (errors.cloudwatch) failed.push('CloudWatch');
  if (errors.serviceQuotas) failed.push('Service Quotas');
  if (errors.sqs) failed.push('SQS ListQueues');
  if (errors.dynamodb) failed.push('DynamoDB (top orgs/users)');

  if (failed.length === 0) return null;

  return (
    <Alert severity="warning" sx={{ mb: 3 }} data-testid="partial-data-banner">
      <AlertTitle>Partial data</AlertTitle>
      The following AWS data sources failed and their panels will show "data unavailable":{' '}
      <strong>{failed.join(', ')}</strong>. Other panels are unaffected.
    </Alert>
  );
};

const Tier1Panels: React.FC<{ health: AwsHealth }> = ({ health }) => {
  // Lambda concurrency vs limit — peak across the selected window so the
  // "how close to the cap" reading reflects the worst case in window, not
  // the last 5 minutes.
  const concPct =
    health.lambda.concurrencyMaxInWindow !== null && health.lambda.concurrencyLimit
      ? (health.lambda.concurrencyMaxInWindow / health.lambda.concurrencyLimit) * 100
      : null;
  const concSeverity: Severity = concPct === null ? 'healthy' : severityFromPercent(concPct);

  const throttleSeverity: Severity = health.lambda.throttles
    ? severityFromAbsolute(health.lambda.throttles, 1, 10)
    : 'healthy';

  const dlqSeverity: Severity = health.sqs.dlqDepthTotal
    ? severityFromAbsolute(health.sqs.dlqDepthTotal, 1, 50)
    : 'healthy';

  const error5xxRate = health.apiGateway.error5xxRate ?? 0;
  const error5xxSeverity: Severity =
    error5xxRate >= 0.01 ? 'critical' : error5xxRate >= 0.001 ? 'warning' : 'healthy';

  const sparkValues = useMemo(
    () => health.lambda.concurrencySpark.map((p) => p.value),
    [health.lambda.concurrencySpark],
  );

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card data-testid="panel-lambda-concurrency">
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Lambda concurrency ({health.window})
              </Typography>
              {severityIcon(concSeverity)}
            </Stack>
            <Typography variant="h3" sx={{ fontWeight: 700 }}>
              {health.lambda.concurrencyMaxInWindow ?? '—'}
              {health.lambda.concurrencyLimit && (
                <Typography component="span" variant="h5" color="text.secondary" sx={{ ml: 1 }}>
                  / {health.lambda.concurrencyLimit}
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Peak in last {health.window} vs region quota
            </Typography>
            {concPct !== null && (
              <LinearProgress
                variant="determinate"
                value={Math.min(100, concPct)}
                sx={{
                  mt: 2,
                  height: 8,
                  borderRadius: 1,
                  '& .MuiLinearProgress-bar': { backgroundColor: severityColor(concSeverity) },
                }}
              />
            )}
            {sparkValues.length > 0 && (
              <Sparkline values={sparkValues} color={severityColor(concSeverity)} />
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card data-testid="panel-lambda-throttles">
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Lambda throttles ({health.window})
              </Typography>
              {severityIcon(throttleSeverity)}
            </Stack>
            <Typography variant="h3" sx={{ fontWeight: 700, color: severityColor(throttleSeverity) }}>
              {health.lambda.throttles ?? '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {throttleSeverity === 'healthy'
                ? `Healthy — no throttles in the last ${health.window}`
                : 'We are hitting the concurrency limit'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Lambda errors ({health.window}): <strong>{health.lambda.errors ?? '—'}</strong>
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card data-testid="panel-dlq-depth">
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="overline" color="text.secondary">
                DLQ messages (total)
              </Typography>
              {severityIcon(dlqSeverity)}
            </Stack>
            <Typography variant="h3" sx={{ fontWeight: 700, color: severityColor(dlqSeverity) }}>
              {health.sqs.dlqDepthTotal}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dlqSeverity === 'healthy'
                ? 'Healthy — no failed jobs sitting in dead-letter queues'
                : `${health.sqs.dlqWithMessages.length} DLQ${
                    health.sqs.dlqWithMessages.length === 1 ? '' : 's'
                  } have undelivered messages`}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card data-testid="panel-apigw-5xx">
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="overline" color="text.secondary">
                API Gateway 5xx rate ({health.window})
              </Typography>
              {severityIcon(error5xxSeverity)}
            </Stack>
            <Typography variant="h3" sx={{ fontWeight: 700, color: severityColor(error5xxSeverity) }}>
              {(error5xxRate * 100).toFixed(2)}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {health.apiGateway.error5xx ?? 0} errors / {health.apiGateway.requests ?? 0} requests
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              p99 latency: <strong>{health.apiGateway.p99LatencyMs?.toFixed(0) ?? '—'} ms</strong>
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const Tier2Panels: React.FC<{ health: AwsHealth }> = ({ health }) => {
  const ddbReadSeverity: Severity = health.dynamodb.readThrottles
    ? severityFromAbsolute(health.dynamodb.readThrottles, 1, 100)
    : 'healthy';
  const ddbWriteSeverity: Severity = health.dynamodb.writeThrottles
    ? severityFromAbsolute(health.dynamodb.writeThrottles, 1, 100)
    : 'healthy';
  const anthropicSeverity: Severity = health.anthropic.throttles
    ? severityFromAbsolute(health.anthropic.throttles, 5, 50)
    : 'healthy';
  const sesPct =
    health.ses.dailyQuota && health.ses.sentLast24h !== null
      ? (health.ses.sentLast24h / health.ses.dailyQuota) * 100
      : null;
  const sesSeverity: Severity = sesPct === null ? 'healthy' : severityFromPercent(sesPct);

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card data-testid="panel-ddb-read-throttles">
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              DDB read throttles ({health.window})
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              {severityIcon(ddbReadSeverity)}
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {health.dynamodb.readThrottles ?? '—'}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card data-testid="panel-ddb-write-throttles">
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              DDB write throttles ({health.window})
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              {severityIcon(ddbWriteSeverity)}
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {health.dynamodb.writeThrottles ?? '—'}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card data-testid="panel-anthropic-throttles">
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Anthropic 429s ({health.window})
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              {severityIcon(anthropicSeverity)}
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {health.anthropic.throttles ?? '—'}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Custom CW metric (LegalAI/AI / AnthropicThrottles)
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <Card data-testid="panel-ses-daily">
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              SES sent (24h)
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              {severityIcon(sesSeverity)}
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                {health.ses.sentLast24h ?? '—'}
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Quota: {health.ses.dailyQuota ?? '—'}/day
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const SqsPanel: React.FC<{ health: AwsHealth }> = ({ health }) => {
  const formatAge = (sec: number): string => {
    if (sec < 60) return `${sec}s`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ${sec % 60}s`;
    return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`;
  };
  const ageSeverity = (sec: number): Severity => severityFromAbsolute(sec, 60, 600);

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card data-testid="panel-sqs-by-age">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              SQS — top queues by oldest message
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {health.sqs.queueCount} queues monitored. Showing the {health.sqs.topByAge.length}{' '}
              with the oldest unprocessed message.
            </Typography>
            {health.sqs.topByAge.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                <HealthyIcon sx={{ color: severityColor('healthy') }} />
                <Typography variant="body2" color="text.secondary">
                  All queues are draining promptly.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Queue</TableCell>
                      <TableCell align="right">Depth</TableCell>
                      <TableCell align="right">Oldest age</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {health.sqs.topByAge.map((row) => (
                      <TableRow key={row.name}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.name}</TableCell>
                        <TableCell align="right">{row.depth}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={formatAge(row.ageSec)}
                            sx={{
                              backgroundColor: severityColor(ageSeverity(row.ageSec)),
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card data-testid="panel-sqs-dlq">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Dead-letter queues with messages
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Anything in a DLQ means a job failed and exhausted all retries.
            </Typography>
            {health.sqs.dlqWithMessages.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2 }}>
                <HealthyIcon sx={{ color: severityColor('healthy') }} />
                <Typography variant="body2" color="text.secondary">
                  All DLQs are empty.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Dead-letter queue</TableCell>
                      <TableCell align="right">Messages</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {health.sqs.dlqWithMessages.map((row) => (
                      <TableRow key={row.name}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.name}</TableCell>
                        <TableCell align="right">
                          <Chip
                            size="small"
                            label={row.depth}
                            sx={{
                              backgroundColor: severityColor('critical'),
                              color: 'white',
                              fontWeight: 600,
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Tier-1 (scale-blind-spot) panels
// ──────────────────────────────────────────────────────────────────────────

const DatabasePanel: React.FC<{ health: AwsHealth }> = ({ health }) => {
  const { aurora } = health;
  if (!aurora.enabled) {
    return (
      <Card sx={{ mb: 3 }} data-testid="panel-aurora-pgvector">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Aurora pgvector
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Not deployed in this environment.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // CPU thresholds: pgvector queries are read-heavy, sustained > 70% means
  // the embedding-search path is choking — that's the user-visible bug.
  const cpuSeverity: Severity =
    aurora.cpuMaxPct === null ? 'healthy' : severityFromAbsolute(aurora.cpuMaxPct, 60, 80);
  // Connection count thresholds: t-shirt sizing without knowing the exact
  // max-connections (depends on ACU). 200 sustained = warning, 500 = critical.
  const connSeverity: Severity =
    aurora.connectionsMaxInWindow === null
      ? 'healthy'
      : severityFromAbsolute(aurora.connectionsMaxInWindow, 200, 500);

  const sparkValues = aurora.connectionsSpark.map((p) => p.value);

  return (
    <Card sx={{ mb: 3 }} data-testid="panel-aurora-pgvector">
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Aurora pgvector — RAG / embeddings DB
          </Typography>
          {severityIcon(connSeverity === 'healthy' && cpuSeverity === 'healthy' ? 'healthy' : connSeverity === 'critical' || cpuSeverity === 'critical' ? 'critical' : 'warning')}
        </Stack>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="overline" color="text.secondary">
              Connections (peak {health.window})
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: severityColor(connSeverity) }}>
              {aurora.connectionsMaxInWindow ?? '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Pool exhaustion = the classic scaling wall
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="overline" color="text.secondary">
              CPU max ({health.window})
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700, color: severityColor(cpuSeverity) }}>
              {aurora.cpuMaxPct?.toFixed(1) ?? '—'}%
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="overline" color="text.secondary">
              ACU consumed
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {aurora.acuConsumed?.toFixed(2) ?? '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Aurora Serverless v2 capacity
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography variant="overline" color="text.secondary">
              Replica lag (max)
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {aurora.replicaLagMaxMs === null ? '—' : `${aurora.replicaLagMaxMs.toFixed(0)} ms`}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {aurora.replicaLagMaxMs === null ? 'No read replicas' : 'Cross-AZ replica lag'}
            </Typography>
          </Grid>
        </Grid>
        {sparkValues.length > 1 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Connection trend ({health.window})
            </Typography>
            <Sparkline values={sparkValues} color={severityColor(connSeverity)} width={800} height={48} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const CognitoPanel: React.FC<{ health: AwsHealth }> = ({ health }) => {
  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {health.cognito.pools.map((pool) => {
        const throttleSeverity: Severity = pool.throttles
          ? severityFromAbsolute(pool.throttles, 1, 10)
          : 'healthy';
        return (
          <Grid key={pool.label} size={{ xs: 12, md: 6 }}>
            <Card data-testid={`panel-cognito-${pool.label}`}>
              <CardContent>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    Cognito ({pool.label}) ({health.window})
                  </Typography>
                  {severityIcon(throttleSeverity)}
                </Stack>
                <Stack direction="row" spacing={4}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Throttles
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: severityColor(throttleSeverity) }}>
                      {pool.throttles ?? '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Sign-ins
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {pool.signInSuccesses ?? '—'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Token refresh
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      {pool.tokenRefreshSuccesses ?? '—'}
                    </Typography>
                  </Box>
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Sign-in default 30 RPS, token-refresh 80 RPS regional limits
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

const LambdaFunctionsPanel: React.FC<{ health: AwsHealth }> = ({ health }) => {
  if (health.lambdaFunctions.length === 0) return null;

  // p99 thresholds: > 5s warning, > 15s critical. These are coarse — most
  // queue handlers run 10s-2min. The signal we care about is *change*.
  const p99Severity = (ms: number | null): Severity => {
    if (ms === null) return 'healthy';
    return severityFromAbsolute(ms, 5000, 15000);
  };

  const formatMs = (ms: number | null): string => {
    if (ms === null) return '—';
    if (ms < 1000) return `${ms.toFixed(0)} ms`;
    return `${(ms / 1000).toFixed(2)} s`;
  };

  return (
    <Card sx={{ mb: 3 }} data-testid="panel-lambda-functions">
      <CardContent>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          Lambda functions — p99 duration ({health.window})
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          A handler creeping from 5s → 25s is the warning before throttles fire.
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Function</TableCell>
                <TableCell align="right">p99 duration</TableCell>
                <TableCell align="right">Invocations</TableCell>
                <TableCell align="right">Errors</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {health.lambdaFunctions.map((fn) => (
                <TableRow key={fn.name}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{fn.name}</TableCell>
                  <TableCell align="right">
                    <Chip
                      size="small"
                      label={formatMs(fn.p99DurationMs)}
                      sx={{
                        backgroundColor: severityColor(p99Severity(fn.p99DurationMs)),
                        color: 'white',
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">{fn.invocations?.toLocaleString() ?? '—'}</TableCell>
                  <TableCell align="right">
                    <span style={{ color: fn.errors && fn.errors > 0 ? severityColor('critical') : 'inherit' }}>
                      {fn.errors?.toLocaleString() ?? '—'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

const ActivityPanel: React.FC<{ health: AwsHealth }> = ({ health }) => {
  return (
    <Grid container spacing={3} sx={{ mt: 1 }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card data-testid="panel-top-orgs">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Top organisations ({health.window})
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              One org accidentally batch-uploading 5000 PDFs would saturate queues silently.
            </Typography>
            {health.topOrgs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No job activity in window.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Organisation</TableCell>
                      <TableCell align="right">Jobs</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {health.topOrgs.map((row) => (
                      <TableRow key={row.organizationId}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{row.organizationId}</TableCell>
                        <TableCell align="right">{row.jobCount.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card data-testid="panel-top-users">
          <CardContent>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Top users ({health.window})
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Spot a runaway script before it impacts everyone else.
            </Typography>
            {health.topUsers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No job activity in window.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Jobs</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {health.topUsers.map((row) => (
                      <TableRow key={row.userId}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {row.userEmail || row.userId}
                        </TableCell>
                        <TableCell align="right">{row.jobCount.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default AwsHealthPage;
